<template>
  <v-container>
    <v-layout row wrap>
      <h1>Users</h1>
      <v-spacer />
      <div>
        <AddUserDialog @userAdded="fetchUsers" />
        <UploadUsersExcelDialog @success="fetchUsers" />
        <DownloadExcelDialog :apiUrl="downloadUsersUrl()" />
      </div>
    </v-layout>
    <br />
    <v-layout row wrap class="mb-3">
      <v-flex sm9>
        <FilterItems :filterOptions="filterOptions" @apply="applyFilters" />
      </v-flex>
      <v-flex sm3>
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Search"
          single-line
          hide-details
          align="center"
          @keypress.enter="fetchUsers"
          @click:append="fetchUsers"
        ></v-text-field>
      </v-flex>
    </v-layout>
    <v-data-table
      :headers="headers"
      :loading="loading"
      :items="users"
      :total-items="totalUsers"
      :pagination.sync="pagination"
      :rows-per-page-items="[10, 20, 50, 100]"
      class="elevation-1"
    >
      <template v-slot:items="{ item }">
        <td class="text-xs-center">{{ item.id }}</td>
        <td class="text-xs-left">
          {{ item.name }}
        </td>
        <td class="text-xs-left">
          <a :href="`tel:${item.phone}`">{{ item.phone }}</a>
        </td>
        <td class="text-xs-left">
          <a :href="`mailto:${item.email}`">{{ item.email }}</a>
        </td>
        <td class="text-xs-left">
          {{ item.created_at.split(" ")[0] }}
        </td>
        <td class="justify-center layout px-0" style="cursor: pointer">
          <router-link
            :to="{
              name: 'UserDetails',
              params: { mobile: item.phone },
            }"
            target="_blank"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon small label="Details" v-on="on" style="color: #229">
                  content_paste_go
                </v-icon>
              </template>
              <span>Details</span>
            </v-tooltip>
          </router-link>
        </td>
      </template>
      <template v-slot:pageText>
        {{ pagination.rowsPerPage }}
        Rows {{ usersPages.from }} - {{ usersPages.to }} of
        {{ usersPages.total }}
      </template>
    </v-data-table>
    <div class="text-xs-center positionLeft">
      <v-pagination
        circle
        v-model="currentPage"
        :length="totalPages"
        @input="fetchUsers"
      ></v-pagination>
    </div>
  </v-container>
</template>

<script>
  import HTTP from "@/utils/HTTP";
  import {
    usersUrl,
    usersFilterOptionsUrl,
    downloadUsersExcelUrl,
  } from "@/utils/variables";
  import { mapGetters } from "vuex";
  import FilterItems from "@/components/FilterItems";
  import AddUserDialog from "@/components/Dialogs/AddUserDialog";
  import DownloadExcelDialog from "@/components/Dialogs/DownloadExcelDialog";
  import UploadUsersExcelDialog from "@/components/Dialogs/UploadUsersExcelDialog";

  export default {
    components: {
      FilterItems,
      AddUserDialog,
      UploadUsersExcelDialog,
      DownloadExcelDialog,
    },
    data: () => ({
      loading: false,
      search: "",
      users: [],

      totalUsers: 0,
      pagination: {
        page: 1,
        rowsPerPage: 10,
      },
      currentPage: 1,
      currentRowsPerPage: 10,
      usersPages: {
        from: 0,
        to: 0,
        total: 0,
      },
      photoUrl: process.env.VUE_APP_PHOTO_URL,
      headers: [
        {
          text: "ID",
          align: "center",
          value: "id",
        },
        // {
        //   text: "Photo",
        //   align: "center",
        //   value: "photo",
        //   sortable: false,
        // },
        {
          text: "Name",
          align: "left",
          value: "name",
        },
        { text: "Phone No.", align: "left", value: "phone" },
        {
          text: "Email",
          align: "left",
          value: "email",
        },
        {
          text: "Joining Date",
          align: "left",
          value: "created_at",
          sortable: false,
        },
        { text: "Details", align: "center", value: "details", sortable: false },
      ],
      orderBy: "user_id",
      order: "desc",
      primaryFetch: true,
      filterOptions: {},
      filterUrlExtension: "",
      color: "",
      errorModal: false,
      errorMsg: "",
      scroll: 0,
    }),

    watch: {
      pagination() {
        this.fetchUsers();
      },
    },

    computed: {
      totalPages() {
        return Math.ceil(this.totalUsers / this.pagination.rowsPerPage);
      },

      ...mapGetters(["backRouteActive"]),
    },

    async created() {
      this.backRouteActive ? await this.setParams() : "";
      await this.fetchFilterOptions();
      this.fetchUsers();
    },

    methods: {
      downloadUsersUrl() {
        return `${downloadUsersExcelUrl}?query=${this.search}${this.filterUrlExtension}&order_by=${this.orderBy}&order=${this.order}`;
      },

      async setParams() {
        this.$route.query.search
          ? (this.search = this.$route.query.search)
          : "";
        this.$route.query.page
          ? (this.pagination.page = +this.$route.query.page)
          : (this.pagination.page = 0);
        this.$route.query.rowsPerPage
          ? (this.pagination.rowsPerPage = this.$route.query.rowsPerPage)
          : (this.pagination.rowsPerPage = 0);
        this.$route.query.scroll
          ? (this.scroll = this.$route.query.scroll)
          : "";
        document.documentElement.scrollTop = this.scroll;
        this.$store.commit("resetBackPageActive");
      },

      async fetchFilterOptions() {
        const { data } = await HTTP().get(usersFilterOptionsUrl);
        this.filterOptions = data.data;
      },

      async fetchUsers() {
        this.loading = true;
        this.filterDialog = false;

        this.currentPage =
          this.backRouteActive && this.page ? this.page : this.currentPage;

        this.pagination.rowsPerPage =
          this.backRouteActive && this.rowsPerPage
            ? parseInt(this.rowsPerPage)
            : this.pagination.rowsPerPage;

        if (!this.primaryFetch) {
          const { sortBy, descending } = this.pagination;

          if (!sortBy) {
            this.orderBy = "user_id";
            this.order = "asc";
          } else {
            sortBy == "id"
              ? (this.orderBy = "user_id")
              : (this.orderBy = sortBy);

            descending ? (this.order = "desc") : (this.order = "asc");
          }
        } else {
          this.pagination.descending = true;
        }

        try {
          const { data } = await HTTP().get(
            `${usersUrl}?query=${this.search}&page=${this.currentPage}&per_page=${this.pagination.rowsPerPage}${this.filterUrlExtension}&order_by=${this.orderBy}&order=${this.order}`
          );
          if (data.data) {
            this.users = data.data.data;
            this.totalUsers = data.data.total;
            this.usersPages.from = data.data.from;
            this.usersPages.to = data.data.to;
            this.usersPages.total = data.data.total;
            this.primaryFetch = false;
          } else {
            this.users = [];
          }
          this.loading = false;
        } finally {
          //
        }
      },

      applyFilters(filterUrlExtension) {
        this.filterUrlExtension = filterUrlExtension;
        this.pagination.page = 1;
        this.currentPage = 1;
        this.fetchUsers();
      },

      // filterUsers() {
      //   this.filterUrlExtension = "";
      //   Object.keys(this.filterTypes).map((type) => {
      //     this.filterTypes[type] &&
      //       (this.filterUrlExtension = `${this.filterUrlExtension}&${type}=${this.filterTypes[type]}`);
      //   });
      //   this.pagination.page = 1;
      //   this.currentPage = 1;
      // },

      goToUserDetails(id) {
        const currentScroll =
          window.pageYOffset || document.documentElement.scrollTop;
        const goUrl = `/user/${id}`;
        const backUrl = `/users?search=${this.search}&page=${
          this.pagination.page
        }&rowsPerPage=${
          this.pagination.rowsPerPage
        }&scroll=${currentScroll.toFixed(3)}`;
        // this.$emit("goto", {
        //   goUrl,
        //   backUrl,
        // });
        const fullUrl = backUrl;
        this.$store.commit("setGoBackRoute", fullUrl);
        this.$router.push(goUrl);
      },
    },
  };
</script>

<style scoped>
  .positionLeft {
    position: relative;
    left: -44px;
  }
  a {
    text-decoration: none;
    margin-top: 16px;
  }
  .filter-icon {
    cursor: pointer;
  }
</style>
